import { asFile, type FileMeta, type ProjectDoc, type RecordData } from '@tomo/core';
import { cx } from '@tomo/ui';
import { Box, FileArchive, FileAudio, FileText, File as FileIcon, Play } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useBlobUrl } from '../../data/blobs.ts';
import { useOptionalSession } from '../../data/hooks.tsx';
import { fileKind } from '../../lib/files.ts';
import './assets.css';

/** The file of an asset record (first `file` field of its collection). */
export function assetFileOf(project: ProjectDoc, record: RecordData | undefined): FileMeta | null {
  if (!record) return null;
  const c = project.getCollection(record.collectionId);
  const f = c?.fields.find((x) => x.type === 'file');
  return f ? asFile(record.values[f.id]) : null;
}

export function Waveform({ peaks, className, style }: { peaks: number[]; className?: string; style?: CSSProperties }) {
  const n = peaks.length || 1;
  return (
    <svg className={cx('waveform', className)} viewBox={`0 0 ${n * 3} 40`} preserveAspectRatio="none" style={style} aria-hidden="true">
      {peaks.map((p, i) => {
        const h = Math.max(1.5, p * 38);
        return <rect key={i} x={i * 3} y={20 - h / 2} width={2} height={h} rx={1} />;
      })}
    </svg>
  );
}

export interface AssetThumbProps {
  file: FileMeta | null;
  className?: string;
  style?: CSSProperties;
  /** Show filename/extension on non-visual files. */
  showLabel?: boolean;
  fit?: 'cover' | 'contain';
}

/** Small visual for any file: image, video poster, audio waveform or a type glyph. */
export function AssetThumb({ file, className, style, showLabel, fit = 'cover' }: AssetThumbProps) {
  const session = useOptionalSession();
  const kind = file ? fileKind(file.mime, file.name) : 'other';
  const visualHash = !file ? null : kind === 'image' ? (file.thumb ?? file.hash) : kind === 'video' ? (file.thumb ?? null) : null;
  const url = useBlobUrl(visualHash, session?.id);
  const ext = file?.name.split('.').pop()?.toUpperCase().slice(0, 4);

  return (
    <div className={cx('asset-thumb', className)} data-kind={kind} data-fit={fit} style={style}>
      {!file ? (
        <FileIcon className="asset-thumb__glyph" />
      ) : kind === 'image' ? (
        url ? <img src={url} alt="" loading="lazy" draggable={false} /> : <span className="asset-thumb__loading" />
      ) : kind === 'video' ? (
        <>
          {url && <img src={url} alt="" loading="lazy" draggable={false} />}
          <span className="asset-thumb__play">
            <Play size={16} fill="currentColor" />
          </span>
        </>
      ) : kind === 'audio' ? (
        file.peaks?.length ? <Waveform peaks={file.peaks} className="asset-thumb__wave" /> : <FileAudio className="asset-thumb__glyph" />
      ) : kind === 'model' ? (
        <Box className="asset-thumb__glyph" />
      ) : kind === 'archive' ? (
        <FileArchive className="asset-thumb__glyph" />
      ) : (
        <FileText className="asset-thumb__glyph" />
      )}
      {showLabel && file && kind !== 'image' && kind !== 'video' && <span className="asset-thumb__ext">{ext}</span>}
    </div>
  );
}
